/**
 * TWAP tab — the pure half (TASK-20260823-twap-strategies-tca, PR-C / AC 25).
 *
 * Everything the strategy workspace's TWAP tab renders is derived here from the list read model
 * (contract: packages/Exchange.Data/Models/TwapReadModels.cs TwapParentSummary, camelCase on the wire) so
 * the tab stays a thin renderer and the rollups are unit-testable. Server-side `GET /api/twap/summary`
 * rollups are an explicit non-goal for v1 — these client rollups ARE the summary.
 *
 * Rules carried over from twapReport.js (spec AC 10 / AC 25):
 *   - a metric with no sample is an honest null rendered as "—", never a fake zero;
 *   - every signed figure carries an explicit sign AND an arrow (gain/loss is never color-only);
 *   - percentages and bps are weighted by FILLED NOTIONAL so a dust parent cannot swing the headline.
 */
import { fmtQuote, fmtPct, fmtBps, isWorking, PACE_BAND_PCT } from './twapReport.js';
import { POSTURE_NAMES } from './twapPolicy.js';

const isNum = (v) => typeof v === 'number' && Number.isFinite(v);
const num = (v) => (isNum(v) ? v : (v === null || v === undefined ? null : (Number.isFinite(Number(v)) ? Number(v) : null)));

/** The filled notional of a parent in quote currency (0 when nothing filled). */
export function filledNotional(parent) {
  const v = num(parent?.filledAmount);
  return v !== null && v > 0 ? v : 0;
}

// ----- rollups --------------------------------------------------------------------------------

/**
 * One rollup over a list of parents:
 *   count · scored (parents with a net-savings figure) · notional (Σ filled amount)
 *   netSavingsQuote (Σ) · netSavingsPct (weighted by notional over scored parents)
 *   feesSaved (Σ) · makerFillPct (weighted by filled notional over parents with a known share) · improvementVsSweepBps (weighted).
 * Every figure is null when it has no sample.
 */
export function rollup(parents) {
  const list = Array.isArray(parents) ? parents : [];
  let notional = 0;
  let scored = 0;
  let net = null;
  let netPctWeighted = 0;
  let netPctWeight = 0;
  let fees = null;
  let makerWeighted = 0;   // Σ makerFillPct · filledAmount over parents with a known maker share
  let makerWeight = 0;     // Σ filledAmount of those parents (quote currency — unit-consistent across symbols)
  let bpsWeighted = 0;
  let bpsWeight = 0;
  let working = 0;

  for (const p of list) {
    const n = filledNotional(p);
    notional += n;
    if (isWorking(p?.status)) working += 1;

    const ns = num(p?.netSavingsQuote);
    if (ns !== null) {
      scored += 1;
      net = (net ?? 0) + ns;
    }
    const nsPct = num(p?.netSavingsPct);
    if (nsPct !== null && n > 0) {
      netPctWeighted += nsPct * n;
      netPctWeight += n;
    }
    const fs = num(p?.feesSaved);
    if (fs !== null) fees = (fees ?? 0) + fs;

    // Maker share is weighted by FILLED NOTIONAL, never by summed base-asset quantity: quantities of
    // different symbols are not addable (one PEPE sell of 9.87 billion units read the whole tab as 0.2 %
    // maker while the executions were ~85 % maker by value — TASK-K2T-20 AC 14). A parent whose maker
    // share is unknown, or with no notional, is excluded from the sample rather than zero-weighted.
    const mp = num(p?.makerFillPct);
    if (mp !== null && n > 0) {
      makerWeighted += mp * n;
      makerWeight += n;
    }
    const bps = num(p?.improvementVsSweepBps);
    if (bps !== null && n > 0) {
      bpsWeighted += bps * n;
      bpsWeight += n;
    }
  }

  return {
    count: list.length,
    working,
    scored,
    notional,
    netSavingsQuote: net,
    netSavingsPct: netPctWeight > 0 ? netPctWeighted / netPctWeight : null,
    feesSaved: fees,
    makerFillPct: makerWeight > 0 ? makerWeighted / makerWeight : null,
    improvementVsSweepBps: bpsWeight > 0 ? bpsWeighted / bpsWeight : null,
  };
}

function toneOf(v) {
  if (!isNum(v) || v === 0) return 'neutral';
  return v > 0 ? 'gain' : 'loss';
}

/** The headline KPI tiles for the tab (one rollup over the whole list). */
export function tabKpis(parents, currency = 'USD') {
  const r = rollup(parents);
  const finished = r.count - r.working;
  const tile = (key, label, value, text, tone, sub = null, hint = null) => ({ key, label, value: isNum(value) ? value : null, text, tone, sub, hint });
  return [
    tile('netSavings', 'Net savings vs sweep', r.netSavingsQuote, fmtQuote(r.netSavingsQuote, currency), toneOf(r.netSavingsQuote),
      isNum(r.netSavingsPct) ? `${fmtPct(r.netSavingsPct)} of filled notional` : null,
      'Σ price improvement + fees saved over finished parents; weighted by filled notional'),
    tile('feesSaved', 'Fees saved', r.feesSaved, fmtQuote(r.feesSaved, currency), toneOf(r.feesSaved),
      r.scored > 0 ? `over ${r.scored} scored parent${r.scored === 1 ? '' : 's'}` : null),
    tile('makerFill', 'Maker fill', r.makerFillPct, fmtPct(r.makerFillPct, { signed: false, decimals: 1 }), 'neutral',
      isNum(r.makerFillPct) ? 'share of filled notional that added liquidity' : null),
    tile('vsSweep', 'vs sweep', r.improvementVsSweepBps, fmtBps(r.improvementVsSweepBps), toneOf(r.improvementVsSweepBps),
      isNum(r.improvementVsSweepBps) ? 'notional-weighted' : null),
    tile('executions', 'Executions', r.count, String(r.count), 'neutral',
      `${r.working} working · ${finished} finished`),
  ];
}

function groupBy(parents, keyOf) {
  const groups = new Map();
  for (const p of (Array.isArray(parents) ? parents : [])) {
    const k = keyOf(p);
    if (!groups.has(k)) groups.set(k, []);
    groups.get(k).push(p);
  }
  return [...groups.entries()].map(([key, items]) => ({ key, ...rollup(items) }));
}

/** Per-symbol rollups, largest filled notional first. */
export function bySymbol(parents) {
  return groupBy(parents, (p) => String(p?.symbol ?? '—')).sort((a, b) => b.notional - a.notional || a.key.localeCompare(b.key));
}

function utcDay(iso) {
  const t = Date.parse(iso ?? '');
  return Number.isNaN(t) ? 'unknown' : new Date(t).toISOString().slice(0, 10);
}

/** Per-UTC-start-day rollups, newest day first (an unparseable start lands in `unknown`, last). */
export function byDay(parents) {
  return groupBy(parents, (p) => utcDay(p?.startedAt)).sort((a, b) => {
    if (a.key === 'unknown') return 1;
    if (b.key === 'unknown') return -1;
    return b.key.localeCompare(a.key);
  });
}

// ----- live parents ---------------------------------------------------------------------------

/** 0–100 filled share: by quantity when TargetQty is set, by amount otherwise, null with neither. */
export function progressPct(parent) {
  const tq = num(parent?.targetQty);
  if (tq !== null && tq > 0) return Math.min(100, Math.max(0, ((num(parent?.filledQty) ?? 0) / tq) * 100));
  const ta = num(parent?.targetAmount);
  if (ta !== null && ta > 0) return Math.min(100, Math.max(0, ((num(parent?.filledAmount) ?? 0) / ta) * 100));
  return null;
}

/** 0–100 elapsed share of StartedAt→EndBy at `nowMs`, clamped; null when the window is unparseable. */
export function timePct(parent, nowMs) {
  const start = Date.parse(parent?.startedAt ?? '');
  const end = Date.parse(parent?.endBy ?? '');
  if (Number.isNaN(start) || Number.isNaN(end) || end <= start || !isNum(nowMs)) return null;
  return Math.min(100, Math.max(0, ((nowMs - start) / (end - start)) * 100));
}

/** `15 m 00 s` under an hour, `1 h 02 m` above, `< 1 s` for sub-second; `—` for a non-number. */
export function fmtDuration(ms) {
  if (!isNum(ms)) return '—';
  if (ms < 1000) return '< 1 s';
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h} h ${String(m).padStart(2, '0')} m`;
  return `${m} m ${String(s).padStart(2, '0')} s`;
}

function paceStateOf(pacePct) {
  if (!isNum(pacePct)) return 'unknown';
  if (pacePct > PACE_BAND_PCT) return 'ahead';
  if (pacePct < -PACE_BAND_PCT) return 'behind';
  return 'on';
}

/** The posture chip label from a summary row: the server's name, else the client's name for the short, else null. */
function postureLabelOf(p) {
  if (typeof p?.recentPostureName === 'string' && p.recentPostureName) return p.recentPostureName;
  const short = num(p?.recentPosture);
  return short !== null ? (POSTURE_NAMES[short] ?? null) : null;
}

/** PaceAdherence (0–1 on the wire) as `98.0 %`; `—` when the parent has no band samples yet. */
function paceAdherenceText(v) {
  return isNum(v) ? fmtPct(v * 100, { signed: false, decimals: 1 }) : '—';
}

/**
 * The live-card view of every Working parent: progress, time, pace (= progress − time; the ± band is
 * PACE_BAND_PCT), ETA from EndBy, cycle text, the posture chip and pace adherence — both read off the
 * LIST row (contract: TwapParentSummary.RecentPosture / RecentPostureName / PaceAdherence, PR-C2), so
 * the tab needs no per-parent detail read.
 */
export function liveParentsView(parents, nowMs) {
  const list = Array.isArray(parents) ? parents.filter((p) => isWorking(p?.status)) : [];
  return list.map((p) => {
    const id = String(p.parentOrderId ?? '');
    const progress = progressPct(p);
    const time = timePct(p, nowMs);
    const pace = isNum(progress) && isNum(time) ? progress - time : null;
    const end = Date.parse(p?.endBy ?? '');
    const etaMs = Number.isNaN(end) ? null : Math.max(0, end - nowMs);
    const cyclesDone = num(p?.cyclesCompleted);
    const cyclesPlanned = num(p?.cyclesPlanned);
    const adherence = num(p?.paceAdherence);
    return {
      id,
      symbol: p.symbol,
      side: p.side,
      status: p.status,
      progressPct: progress,
      timePct: time,
      pacePct: pace,
      paceState: paceStateOf(pace),
      paceText: fmtPct(pace, { decimals: 1 }),
      etaMs,
      etaText: etaMs === null ? '—' : (etaMs === 0 && !Number.isNaN(end) && nowMs > end ? 'past end-by' : fmtDuration(etaMs)),
      cyclesText: cyclesDone === null ? '—' : `${cyclesDone}${cyclesPlanned !== null ? ` / ${cyclesPlanned}` : ''}`,
      postureLabel: postureLabelOf(p),
      paceAdherence: adherence,
      paceAdherenceText: paceAdherenceText(adherence),
      cancelRequested: Boolean(p?.cancelRequestedAt),
      summary: p,
    };
  });
}

// ----- PR-C2: cumulative savings ---------------------------------------------------------------

/**
 * The running total of net savings over FINISHED, scored parents in EndedAt order — the "what has
 * TWAP earned this strategy so far" line. A parent with no net figure (still Working, or unscored)
 * or an unparseable EndedAt is skipped and counted in `skipped`, never drawn as a zero step.
 * `total` is null (not 0) when nothing is scored.
 */
export function cumulativeSavingsSeries(parents) {
  const list = Array.isArray(parents) ? parents : [];
  let skipped = 0;
  const scored = [];
  for (const p of list) {
    const delta = num(p?.netSavingsQuote);
    const t = Date.parse(p?.endedAt ?? '');
    if (isWorking(p?.status) || delta === null || Number.isNaN(t)) { skipped += 1; continue; }
    scored.push({ t, id: String(p.parentOrderId ?? ''), symbol: p.symbol ?? null, side: p.side ?? null, delta });
  }
  scored.sort((a, b) => a.t - b.t || a.id.localeCompare(b.id));
  let cumulative = 0;
  const points = scored.map((s) => { cumulative += s.delta; return { ...s, cumulative }; });
  return { points, total: points.length > 0 ? cumulative : null, count: points.length, skipped };
}

// ----- PR-C2: posture mix ----------------------------------------------------------------------

/**
 * One posture mix over many scorecards (contract: TwapScorecardResult.PostureMix → TwapPostureShare
 * { posture, postureName, qty, pct }): filled qty summed per posture, `pct` of the merged total,
 * `parents` = how many scorecards traded under that posture; most-filled first. A share with no
 * positive qty or an unknown posture short is ignored; the name falls back to POSTURE_NAMES.
 */
export function postureMixRollup(scorecards) {
  const acc = new Map();
  for (const sc of (Array.isArray(scorecards) ? scorecards : [])) {
    for (const m of (Array.isArray(sc?.postureMix) ? sc.postureMix : [])) {
      const posture = num(m?.posture);
      const qty = num(m?.qty);
      if (posture === null || !Number.isInteger(posture) || qty === null || qty <= 0) continue;
      const cur = acc.get(posture) ?? { posture, postureName: m.postureName ?? POSTURE_NAMES[posture] ?? String(posture), qty: 0, parents: 0 };
      cur.qty += qty;
      cur.parents += 1;
      acc.set(posture, cur);
    }
  }
  const total = [...acc.values()].reduce((s, m) => s + m.qty, 0);
  return [...acc.values()]
    .sort((a, b) => b.qty - a.qty || a.posture - b.posture)
    .map((m) => ({ posture: m.posture, postureName: m.postureName, qty: m.qty, pct: total > 0 ? (m.qty / total) * 100 : null, parents: m.parents }));
}

// ----- PR-C2: slice latency --------------------------------------------------------------------

/**
 * Slice latency over many scorecards (contract: TwapScorecardResult.SliceLatencyP50Ms / P95Ms /
 * ChildCount). Per-parent percentiles cannot be re-percentiled without the raw samples, so the tab
 * reports: p50 = the child-count-weighted MEDIAN of the parents' p50s (a parent with no child count
 * weighs 1); p95 = the WORST parent's p95. Both null when no parent carries a figure.
 */
export function latencyRollup(scorecards) {
  const rows = [];
  for (const sc of (Array.isArray(scorecards) ? scorecards : [])) {
    const p50 = num(sc?.sliceLatencyP50Ms);
    const p95 = num(sc?.sliceLatencyP95Ms);
    if (p50 === null && p95 === null) continue;
    const w = num(sc?.childCount);
    rows.push({ p50, p95, w: w !== null && w > 0 ? w : 1 });
  }
  if (rows.length === 0) return { p50Ms: null, p95Ms: null, parents: 0, children: 0 };
  const children = rows.reduce((s, r) => s + r.w, 0);
  const withP50 = rows.filter((r) => r.p50 !== null).sort((a, b) => a.p50 - b.p50);
  let p50Ms = null;
  if (withP50.length > 0) {
    const half = withP50.reduce((s, r) => s + r.w, 0) / 2;
    let seen = 0;
    for (const r of withP50) { seen += r.w; if (seen >= half) { p50Ms = r.p50; break; } }
  }
  const p95s = rows.filter((r) => r.p95 !== null).map((r) => r.p95);
  return { p50Ms, p95Ms: p95s.length > 0 ? Math.max(...p95s) : null, parents: rows.length, children };
}

// ----- PR-C2: maker-capture heatmap ------------------------------------------------------------

/** Day-of-week labels indexed by the wire's `dayOfWeek` (.NET DayOfWeek: Sunday = 0). */
export const DAY_LABELS = Object.freeze(['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']);
export const HOURS = Object.freeze(Array.from({ length: 24 }, (_, h) => h));

/**
 * The 7×24 grid behind the maker-capture heatmap (contract: GET /api/twap/heatmap → { cells:
 * [{ dayOfWeek 0–6, hour 0–23, makerFillPct, fills }] }; the raw envelope or the bare array is
 * accepted). `rows[day][hour]` is `{ makerFillPct, fills }` or null when nothing filled then. A
 * repeated (day, hour) merges by fills-weighted maker share; an out-of-range or malformed cell is
 * dropped. `makerFillPct` stays null when the server had fills but no maker flag (drawn hatched).
 */
export function heatmapGrid(cells) {
  const list = Array.isArray(cells) ? cells : (Array.isArray(cells?.cells) ? cells.cells : []);
  const rows = DAY_LABELS.map(() => HOURS.map(() => null));
  let fills = 0;
  let maxFills = 0;
  for (const c of list) {
    const day = num(c?.dayOfWeek);
    const hour = num(c?.hour);
    const n = num(c?.fills);
    if (day === null || hour === null || !Number.isInteger(day) || !Number.isInteger(hour) || day < 0 || day > 6 || hour < 0 || hour > 23) continue;
    const count = n !== null && n > 0 ? n : 0;
    const pct = num(c?.makerFillPct);
    const cur = rows[day][hour];
    if (!cur) {
      rows[day][hour] = { makerFillPct: pct, fills: count };
    } else {
      const known = [[cur.makerFillPct, cur.fills], [pct, count]].filter(([v, w]) => v !== null && w > 0);
      const weight = known.reduce((s, [, w]) => s + w, 0);
      rows[day][hour] = {
        makerFillPct: weight > 0 ? known.reduce((s, [v, w]) => s + v * w, 0) / weight : (cur.makerFillPct ?? pct),
        fills: cur.fills + count,
      };
    }
    fills += count;
    maxFills = Math.max(maxFills, rows[day][hour].fills);
  }
  return { rows, hasData: rows.some((r) => r.some((c) => c !== null)), fills, maxFills };
}

// ----- sort / filter --------------------------------------------------------------------------

const NUMERIC_KEYS = new Set(['filledAmount', 'filledQty', 'netSavingsQuote', 'netSavingsPct', 'feesSaved', 'makerFillPct', 'improvementVsSweepBps', 'totalFees', 'childCount']);
const TIME_KEYS = new Set(['startedAt', 'endBy', 'endedAt']);
const STRING_KEYS = new Set(['symbol', 'status', 'side', 'source']);

/** Newest StartedAt first (the list's default order). */
export function newestFirst(parents) {
  return sortParents(parents, 'startedAt', 'desc');
}

/** A sorted COPY: numerics/times with nulls last in either direction, strings case-insensitive; unknown key → as-is. */
export function sortParents(parents, key, dir = 'asc') {
  const list = Array.isArray(parents) ? [...parents] : [];
  const sign = dir === 'desc' ? -1 : 1;
  let valueOf;
  if (NUMERIC_KEYS.has(key)) valueOf = (p) => num(p?.[key]);
  else if (TIME_KEYS.has(key)) valueOf = (p) => { const t = Date.parse(p?.[key] ?? ''); return Number.isNaN(t) ? null : t; };
  else if (STRING_KEYS.has(key)) valueOf = (p) => (p?.[key] == null ? null : String(p[key]).toLowerCase());
  else return list;

  return list.sort((a, b) => {
    const va = valueOf(a);
    const vb = valueOf(b);
    if (va === null && vb === null) return 0;
    if (va === null) return 1; // nulls last regardless of direction
    if (vb === null) return -1;
    if (va < vb) return -1 * sign;
    if (va > vb) return 1 * sign;
    return 0;
  });
}

export const STATUS_FILTERS = Object.freeze([
  { key: 'all', label: 'All' },
  { key: 'working', label: 'Working' },
  { key: 'finished', label: 'Finished' },
]);

/** Filter by status group (`all`/`working`/`finished`) or an exact durable status, plus symbol and source. */
export function filterParents(parents, { status = 'all', symbol = null, source = null } = {}) {
  const list = Array.isArray(parents) ? parents : [];
  return list.filter((p) => {
    if (status === 'working' && !isWorking(p?.status)) return false;
    if (status === 'finished' && isWorking(p?.status)) return false;
    if (status && status !== 'all' && status !== 'working' && status !== 'finished' && p?.status !== status) return false;
    if (symbol && p?.symbol !== symbol) return false;
    if (source && p?.source !== source) return false;
    return true;
  });
}

// ----- SSE hint ------------------------------------------------------------------------------

/**
 * Does a `twap-progress` payload (contract: AutoTradeFeedPoller.PollTwapAsync) belong to this strategy?
 * Matched on `requestId` (GUID, case-insensitive) — a manual parent (null requestId) never matches, and
 * a malformed payload or missing strategy id never matches and never throws.
 */
export function tickTouchesStrategy(payload, requestId) {
  if (!payload || typeof payload !== 'object' || !requestId) return false;
  const rid = payload.requestId ?? payload.RequestId;
  if (typeof rid !== 'string' || !rid) return false;
  return rid.toLowerCase() === String(requestId).toLowerCase();
}
